import React from 'react';
import {
  classNames,
  flowClassNames,
  FlowClassNameProps,
  layoutClassNames,
  LayoutClassNameProps,
  surfaceClassNames,
  SurfaceShadow,
  textClassNames,
  TextClassNameProps,
} from './classNames';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark'
  | 'link';

export type ButtonSize = 'sm' | 'lg';
export type ButtonRadius = 'circle' | 'pill' | 'default' | false;

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    FlowClassNameProps,
    LayoutClassNameProps,
    TextClassNameProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  outline?: boolean;
  active?: boolean;
  radius?: ButtonRadius;
  fullWidth?: boolean;
  shadow?: SurfaceShadow;
  isLoading?: boolean;
  loadingLabel?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'primary',
  size,
  outline = false,
  active = false,
  radius = 'default',
  fullWidth = false,
  shadow,
  isLoading = false,
  loadingLabel = 'Chargement...',
  type = 'button',
  disabled,
  className,
  children,
  display,
  flexGrow,
  margin,
  marginTop,
  marginEnd,
  marginBottom,
  marginStart,
  marginX,
  marginY,
  padding,
  paddingTop,
  paddingEnd,
  paddingBottom,
  paddingStart,
  paddingX,
  paddingY,
  textAlign,
  textColor,
  textDecoration,
  textReset,
  overflowY,
  ...props
}, ref) => {
  const variantClass = variant ? `btn-${outline ? 'outline-' : ''}${variant}` : '';

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || isLoading}
      className={classNames(
        'btn',
        variantClass,
        size ? `btn-${size}` : undefined,
        active ? 'active' : undefined,
        radius === 'circle' ? 'rounded-circle' : undefined,
        radius === 'pill' ? 'rounded-pill' : undefined,
        fullWidth ? 'w-100' : undefined,
        flowClassNames({
          display,
          flexGrow,
          overflowY,
        }),
        surfaceClassNames({ shadow }),
        textClassNames({
          textColor,
          textDecoration,
          textReset,
        }),
        layoutClassNames({
          margin,
          marginTop,
          marginEnd,
          marginBottom,
          marginStart,
          marginX,
          marginY,
          padding,
          paddingTop,
          paddingEnd,
          paddingBottom,
          paddingStart,
          paddingX,
          paddingY,
          textAlign,
        }),
        className,
      )}
      {...props}
    >
      {isLoading ? loadingLabel : children}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
